import { useEffect, useState } from 'react';
import { Select } from 'antd';
import { PlusCircleIcon, CloseIcon, ShieldCheckIcon } from '../../../assets/icons';

export interface NewLevel {
  name: string;
  parent: string;
  description: string;
  active: boolean;
}

interface AddLevelModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (level: NewLevel) => void;
  /** Existing level names offered as parent options. */
  parents?: string[];
}

const FIELD =
  'w-full rounded-lg border border-[#c2c6d6] bg-[#eef4ff] px-[17px] py-3.5 text-sm text-ink placeholder:text-[#9ca3af] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

export default function AddLevelModal({ open, onClose, onCreate, parents = [] }: AddLevelModalProps) {
  const [name, setName] = useState('');
  const [parent, setParent] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);

  // Reset the form each time the modal is opened.
  useEffect(() => {
    if (open) {
      setName('');
      setParent('');
      setDescription('');
      setActive(true);
    }
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = () => {
    if (!name.trim()) return;
    onCreate({ name: name.trim(), parent, description: description.trim(), active });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(11,28,48,0.2)] p-6 backdrop-blur-[2px]"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-level-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[560px] overflow-hidden rounded-2xl bg-white shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1),0px_8px_10px_-6px_rgba(0,0,0,0.1)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-[#d3e4fe] px-8 py-6">
          <div className="flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
              <PlusCircleIcon className="size-5" />
            </span>
            <div className="flex flex-col">
              <h2
                id="add-level-title"
                className="text-2xl font-bold leading-[30px] tracking-[-0.48px] text-ink"
              >
                Add New Organization Level
              </h2>
              <p className="text-[11px] font-medium leading-[14px] text-ink">
                Define a new tier for your organizational structure
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="shrink-0 text-ink transition-colors hover:text-brand"
          >
            <CloseIcon className="size-[14px]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-6 p-8">
          {/* Level Name */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="level-name"
              className="flex items-center gap-1 text-xs font-semibold tracking-[0.6px] text-ink"
            >
              Level Name
              <span className="text-[#ba1a1a]">*</span>
            </label>
            <input
              id="level-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Ward, Zone, or Region"
              className={FIELD}
            />
          </div>

          {/* Parent Level */}
          <div className="flex flex-col gap-2">
            <label htmlFor="parent-level" className="text-xs font-semibold tracking-[0.6px] text-ink">
              Parent Level
            </label>
            <Select
              id="parent-level"
              className="field-tint w-full"
              placeholder="Select Parent Level"
              value={parent || undefined}
              onChange={(v) => setParent(v)}
              options={parents.map((p) => ({ value: p, label: p }))}
            />
          </div>

          {/* Description */}
          <div className="flex flex-col gap-2">
            <label htmlFor="level-desc" className="text-xs font-semibold tracking-[0.6px] text-ink">
              Description
            </label>
            <textarea
              id="level-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Briefly describe the purpose of this level..."
              className={[FIELD, 'resize-none'].join(' ')}
            />
          </div>

          {/* Level Status */}
          <div className="flex items-center justify-between gap-4 rounded-xl bg-[#eef4ff] p-4">
            <div className="flex items-center gap-3">
              <ShieldCheckIcon className="h-5 w-4 shrink-0 text-brand" />
              <div className="flex flex-col">
                <span className="text-base font-semibold leading-6 text-ink">Level Status</span>
                <span className="text-[11px] font-medium leading-[14px] text-ink">
                  Set whether this level is currently active
                </span>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={active}
              onClick={() => setActive((v) => !v)}
              className={[
                'relative h-6 w-12 shrink-0 rounded-full transition-colors',
                active ? 'bg-brand' : 'bg-[#c2c6d6]',
              ].join(' ')}
            >
              <span
                className={[
                  'absolute top-0.5 size-5 rounded-full bg-white transition-all',
                  active ? 'left-[26px]' : 'left-0.5',
                ].join(' ')}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-[#d3e4fe] bg-[#eef4ff] px-8 pb-6 pt-[25px]">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[#727785] px-[25px] py-[11px] text-xs font-semibold tracking-[0.6px] text-ink transition-colors hover:bg-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!name.trim()}
            className="rounded-lg bg-brand px-8 py-2.5 text-xs font-semibold tracking-[0.6px] text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            Create Level
          </button>
        </div>
      </div>
    </div>
  );
}
